import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dropdown, Menu, Modal, message } from "antd";
import MyHeader from "./Header.module.css";
import MembersDropdown from "../Members/MembersDropdown";
import MembersGroup from "../Members/MembersGroup";
import TemplateActiveToggle from "../TemplateToggle/TemplateActiveToggle";
import TemplateThumbnail from "../TemplateThumbnail/templateThumbnail";
import { useOfflineToggle } from "../OfflineToggle/useOfflineToggle";
import { openVersionModal, openShareModal } from "../../app/EditorSlice";
import { workwiseServices } from "../../api";
import shareBtn from "../../assets/share-file.svg";
import documentVersionIcon from "../../assets/document-version.png";
import completeIcon from "../../assets/complete.png";
import darkIcon from "../../assets/Dark-Mode.svg";

/**
 * Compact hamburger menu shown in the document header.
 * Owns its own open/close state and reads what it needs from the store;
 * the parent only tells it how to open the app launcher (whose state lives
 * alongside the launcher itself in the header).
 */
const HeaderMenu = ({ onOpenAppLauncher }) => {
    const dispatch = useDispatch();
    const documentInformation = useSelector((state) => state.editor.documentInformation);
    const userInformation = useSelector((state) => state.editor.userInformation);
    const isReadOnly = useSelector((state) => state.editor.isReadOnly);
    const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
    const members = (documentInformation?.members || []).filter((m) => m.id !== userInformation?.userId);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { offline, isBrowserOnline, toggle: toggleOffline } = useOfflineToggle();

    const handleComplete = async () => {
        Modal.confirm({
            title: "Complete Document",
            content: "Are you sure you want to complete the document? This action cannot be undone.",
            okText: "Complete",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const { referenceId } = documentInformation || {};
                    if (referenceId) {
                        await workwiseServices.completeDocument(referenceId);
                        message.success("Document completed successfully.");
                    }
                } catch (e) {
                    console.error(e);
                    message.error("Failed to complete the document. Please try again.");
                }
            },
        });
    };

    const menu = (
        <Menu className={MyHeader.compactMenu}>
            {!isReadOnly && !isTemplateDocument && (
                <Menu.Item
                    key="offline"
                    disabled={!isBrowserOnline}
                    onClick={() => { toggleOffline(); setDropdownOpen(false); }}
                >
                    <div className={MyHeader.menuItemContent}>
                        <span className={`${MyHeader.menuIconBox} ${offline ? MyHeader.iconBoxAmber : MyHeader.iconBoxGreen}`}>
                            <span className={`${MyHeader.statusDot} ${offline ? MyHeader.statusDotOffline : MyHeader.statusDotOnline}`} />
                        </span>
                        <span className={MyHeader.menuIconLabel}>{offline ? 'Offline' : 'Online'}</span>
                        <span className={`${MyHeader.statusPill} ${offline ? MyHeader.statusPillOffline : MyHeader.statusPillOnline}`}>
                            {offline ? 'Working offline' : 'Synced'}
                        </span>
                    </div>
                </Menu.Item>
            )}
            {!isReadOnly && !isTemplateDocument && (
                <Menu.Item key="complete" onClick={handleComplete}>
                    <div className={MyHeader.menuItemContent}>
                        <span className={MyHeader.menuIconBox}>
                            <img src={completeIcon} alt="" className={MyHeader.menuIcon} />
                        </span>
                        <span className={MyHeader.menuIconLabel}>Complete Document</span>
                    </div>
                </Menu.Item>
            )}
            {members.length > 0 && !isTemplateDocument && (
                <Menu.SubMenu key="members" popupClassName={MyHeader.membersSubPopup} title={<div className={MyHeader.menuItemContent}>
                    <span className={`${MyHeader.menuIconBox} member-avatar-container-small`}><MembersGroup size={16} /></span>
                    <span className={MyHeader.menuIconLabel}>Members</span>
                    <span className={MyHeader.menuCount}>{members.length}</span></div>}>
                    <Menu.Item key="members-preview" disabled style={{ cursor: 'default', padding: 4 }}>
                        <MembersDropdown bare />
                    </Menu.Item>
                </Menu.SubMenu>
            )}
            {!isReadOnly && !isTemplateDocument && (
                <Menu.Item key="versions" onClick={() => dispatch(openVersionModal())}>
                    <div className={MyHeader.menuItemContent}>
                        <span className={MyHeader.menuIconBox}>
                            <img src={documentVersionIcon} alt="" className={MyHeader.menuIcon} />
                        </span>
                        <span className={MyHeader.menuIconLabel}>Versions</span>
                    </div>
                </Menu.Item>
            )}
            {!isReadOnly && documentInformation.createBy == userInformation.userId && !isTemplateDocument && (
                <Menu.Item key="share" onClick={() => { dispatch(openShareModal()); setDropdownOpen(false); }}>
                    <div className={MyHeader.menuItemContent}>
                        <span className={MyHeader.menuIconBox}>
                            <img src={shareBtn} alt="" className={MyHeader.menuIcon} />
                        </span>
                        <span className={MyHeader.menuIconLabel}>Share</span>
                    </div>
                </Menu.Item>
            )}
            <Menu.Divider className={MyHeader.menuDivider} />
            <Menu.Item key="mode" onClick={() => { /* keep Modes toggle simple */ }}>
                <div className={MyHeader.menuItemContent}>
                    <span className={MyHeader.menuIconBox}>
                        <img src={darkIcon} alt="" className={MyHeader.menuIcon} />
                    </span>
                    <span className={MyHeader.menuIconLabel}>Mode</span>
                </div>
            </Menu.Item>
            <Menu.Item key="app-launcher" onClick={() => { onOpenAppLauncher?.(); setDropdownOpen(false); }}>
                <div className={MyHeader.menuItemContent}>
                    <span className={MyHeader.menuIconBox}>
                        <svg className={MyHeader.menuIcon} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                    </span>
                    <span className={MyHeader.menuIconLabel}>Apps</span>
                </div>
            </Menu.Item>
            {isTemplateDocument && (
                <Menu.Item key="template-toggle">
                    <TemplateActiveToggle viewMode="menu" onMenuClick={() => setDropdownOpen(false)} />
                </Menu.Item>
            )}
            {isTemplateDocument && (
                <Menu.Item key="template-thumbnail">
                    <TemplateThumbnail viewMode="menu" onMenuClick={() => setDropdownOpen(false)} />
                </Menu.Item>
            )}
        </Menu>
    );

    return (
        <Dropdown overlay={menu} trigger={['click']} visible={dropdownOpen} onVisibleChange={(vis) => setDropdownOpen(vis)} placement="bottomRight">
            <button
                type="button"
                className={`${MyHeader.hamburgerBtn} ${dropdownOpen ? MyHeader.hamburgerBtnActive : ''}`}
                aria-label="Menu"
                aria-expanded={dropdownOpen}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
            </button>
        </Dropdown>
    );
};

export default HeaderMenu;
